/**
 * THE ARITHMETIC BEHIND THE SOURCES SCREEN, in a file with no React in it.
 *
 * `screens/Sources.tsx` draws report A; this decides what its numbers mean, and it is separate
 * from the screen because none of these rules needs a browser to be true. Priority 7 of the
 * business brief is knowing which channel is worth money, and every rule here is about not
 * printing a number the data does not support.
 *
 *   THE WINDOW IS CALENDAR MONTHS, AND NEVER A CLOCK. `periodWindow` is handed the server's own
 *   day (`GET /api/me` answers with `today` in the business zone) and does month arithmetic on
 *   the year and the month rather than on a count of days. "Last month" on the 31st of March is
 *   the whole of February, all twenty-eight or twenty-nine days of it, and subtracting thirty
 *   days would have said the 1st of March. Nothing in this file reads `Date.now()`.
 *
 *   A DASH IS NOT A ZERO. A channel nobody has typed a spend against shows a dash, because "we
 *   have not recorded what this cost" and "this cost nothing" are different facts and only one of
 *   them is knowable from an empty table. The sources the plan budgets at nothing say `free`
 *   instead, which is the second fact, read off the planning figure rather than guessed. A
 *   division with nothing under the line is a dash for the same reason.
 *
 *   TWICE THE TOP IS SHOWN AND NEVER JUDGED. The plan's rule is that a source costing more than
 *   twice its planning figure gets its kill rule applied; the number is put beside the real one
 *   so a person can see it. Nothing in report A compares them, because a rule enforced in two
 *   places is a rule that will disagree with itself. A free source carries no twice-the-top line
 *   at all: twice nothing is nothing.
 *
 *   AND NOW REPORT B, WHICH ARRIVES ALREADY JUDGED. State 3 of section 8 put the thresholds and
 *   their counters on the server, in one frozen constant with a hash of the document it copies,
 *   and `GET /api/reports/kill-rules` answers with what is left, the day a rule would fire and
 *   whether it is within reach. Nothing below recomputes any of that. What lives here is how the
 *   answer is SPELLED: how many leads is written "3 leads", where a dash stands instead of a
 *   number, which cell may be coral, and whether the Today panel is drawn at all. One expression
 *   in this file decides the colour, and it reads a boolean the server sent.
 */
import { CHANNELS, type Channel, type ExpenseCategory } from '@fk/shared';
import type { KillRuleId, KillRuleUnit } from './api.ts';
import { daysBetween, formatDay, isDay, money } from './format.ts';
import { CHANNEL_LABELS } from './labels.ts';

/** Two calendar days, both ends included, which is what both report endpoints take. */
export interface DayWindow {
  readonly from: string;
  readonly to: string;
}

/* ------------------------------------------------------------------ *
 * The period, and the two days it means.
 * ------------------------------------------------------------------ */

/** The four windows worth one press. `custom` is the fifth choice and is two typed days. */
export type FixedPeriod = 'this_month' | 'last_month' | 'last_3_months' | 'last_12_months';
export type Period = FixedPeriod | 'custom';

export const PERIODS: readonly Period[] = [
  'this_month',
  'last_month',
  'last_3_months',
  'last_12_months',
  'custom',
];

export const PERIOD_LABELS: Record<Period, string> = Object.freeze({
  this_month: 'This month',
  last_month: 'Last month',
  last_3_months: 'Last 3 months',
  last_12_months: 'Last 12 months',
  custom: 'Two days I choose',
});

/** What the screen opens on. Long enough that a business at three households has something to see. */
export const FIRST_PERIOD: FixedPeriod = 'last_12_months';

const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

function partsOf(day: string): [number, number, number] | null {
  const match = DAY.exec(day);
  if (!match) return null;
  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

function written(year: number, month: number, date: number): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(
    date,
  ).padStart(2, '0')}`;
}

/**
 * How many days that month held. February is the whole reason this exists: the last day of a
 * month is not a constant, and a window that ends on the 28th of a leap February loses a day of
 * households from the month it is reporting on.
 */
export function daysInMonth(year: number, month: number): number {
  if (month === 2) return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 29 : 28;
  return month === 4 || month === 6 || month === 9 || month === 11 ? 30 : 31;
}

/** The first day of the month `back` months before the one `day` falls in. */
export function monthStart(day: string, back = 0): string {
  const parts = partsOf(day);
  if (!parts) return day;
  const [year, month] = parts;
  const index = year * 12 + (month - 1) - back;
  return written(Math.floor(index / 12), (index % 12) + 1, 1);
}

/** The last day of that same month, counted rather than assumed. */
export function monthEnd(day: string, back = 0): string {
  const parts = partsOf(monthStart(day, back));
  if (!parts) return day;
  const [year, month] = parts;
  return written(year, month, daysInMonth(year, month));
}

/**
 * The window a period means, given the server's day. The three that run up to now end on `today`
 * rather than at the end of the month, because a month that has not finished should not be
 * reported as though it had.
 */
export function periodWindow(period: FixedPeriod, today: string): DayWindow {
  switch (period) {
    case 'this_month':
      return { from: monthStart(today), to: today };
    case 'last_month':
      return { from: monthStart(today, 1), to: monthEnd(today, 1) };
    case 'last_3_months':
      return { from: monthStart(today, 2), to: today };
    case 'last_12_months':
      return { from: monthStart(today, 11), to: today };
  }
}

/**
 * Two typed days, or nothing at all. A half-typed box and a window that runs backwards are both
 * answered by drawing no report rather than by asking the server a question it will refuse: the
 * screen knows what a day is, and a red sentence for a date somebody is halfway through typing is
 * worse than the last good report staying where it was.
 */
export function customWindow(from: string, to: string): DayWindow | null {
  if (!isDay(from) || !isDay(to)) return null;
  return from <= to ? { from, to } : null;
}

/* ------------------------------------------------------------------ *
 * The money, and the two things an empty cell can mean.
 * ------------------------------------------------------------------ */

/** What a cell says when there is nothing to count and nothing to divide by. */
export const NOTHING_TO_SAY = '-';

/** What a cell says for a source the plan pays nothing for. */
export const COSTS_NOTHING = 'free';

/** The two figures a screen needs off a planning entry. Both endpoints answer with these names. */
export interface PlanningCents {
  readonly lowCents: number | null;
  readonly highCents: number | null;
}

/**
 * A source budgeted at nothing: personal network, realtors, and the two community groups. What
 * they cost is somebody's own time, which is not an expense row and never will be, so their cost
 * column is a fact rather than a gap.
 */
export function costsNothingByPlan(figure: PlanningCents | null): boolean {
  return figure !== null && figure.lowCents === 0 && figure.highCents === 0;
}

/** What the channel cost in the window: the money, `free`, or a dash for nobody has said. */
export function costWords(costCents: number, figure: PlanningCents | null): string {
  if (costCents > 0) return money(costCents);
  return costsNothingByPlan(figure) ? COSTS_NOTHING : NOTHING_TO_SAY;
}

/**
 * The cost divided by a count of households, rounded to the cent. Nothing under the line is a
 * dash and never a zero, and a source that costs nothing costs nothing per household too.
 */
export function costEachWords(
  costCents: number,
  count: number,
  figure: PlanningCents | null,
): string {
  if (count <= 0) return NOTHING_TO_SAY;
  if (costCents > 0) return money(Math.round(costCents / count));
  return costsNothingByPlan(figure) ? COSTS_NOTHING : NOTHING_TO_SAY;
}

/** Whole dollars where the cents are all zero, which every budgeted figure in the plan is. */
function budgeted(cents: number): string {
  const words = money(cents);
  return words.endsWith('.00') ? words.slice(0, -3) : words;
}

/** "$300 to $450", or "$110" where the plan gives one figure rather than a range. */
export function planningRangeWords(figure: PlanningCents | null): string | null {
  if (figure === null || figure.lowCents === null || figure.highCents === null) return null;
  const low = budgeted(figure.lowCents);
  const high = budgeted(figure.highCents);
  return low === high ? low : `${low} to ${high}`;
}

/** Twice the top of the range, which is where the plan's own rule sits. Nothing for a free one. */
export function twiceTheTopWords(figure: PlanningCents | null): string | null {
  if (figure === null || figure.highCents === null || figure.highCents <= 0) return null;
  return budgeted(figure.highCents * 2);
}

/**
 * THE TWO REVIEW NUMBERS DO NOT MEAN THE SAME THING, so they are not written as though they did.
 * Reviews asked is a count inside the window, because asking has a date on it. Reviews given is
 * every household on that channel whose review link this system holds, ever: what is kept is a
 * link and a link carries no date, so there is nothing to put a window against. Statement 7 of
 * `api/src/reports/sources.ts` says so in its own comment and does it on purpose.
 *
 * Two numbers of different kinds in one cell, written the same way, is the cell lying about one of
 * them: "0 asked, 1 given" under a caption about eight days reads as a review given in those eight
 * days, and the household it came from may have been on the book since 2025. So the second number
 * carries its own words wherever it is printed, and the caption under the table says it too.
 */
export function reviewsGivenWords(given: number): string {
  return `${given} given to date`;
}

/** Both numbers on one line, for a card and for the panel. */
export function reviewsWords(asked: number, given: number): string {
  return `${asked} asked, ${reviewsGivenWords(given)}`;
}

/** How many of the leads became bookings. A dash where there were no leads to book. */
export function leadToBookedWords(leads: number, booked: number): string {
  if (leads <= 0) return NOTHING_TO_SAY;
  return `${Math.round((booked / leads) * 100)}%`;
}

/** The words on a channel, one spelling, the same one the client book and the enquiries use. */
export function channelWords(channel: Channel): string {
  return CHANNEL_LABELS[channel];
}

/** A roll-up row nobody typed a detail on. Not blank, because a blank row reads as a broken one. */
export const NO_DETAIL = 'no words recorded';

export function detailWords(detail: string | null): string {
  return detail === null || detail.trim() === '' ? NO_DETAIL : detail;
}

/** The seven counts a roll-up row carries. There is no cost: expenses carry no detail. */
export interface DetailNumbers {
  readonly leads: number;
  readonly booked: number;
  readonly households: number;
  readonly recurring: number;
  readonly reviewsAsked: number;
  readonly reviewsGiven: number;
  readonly ended: number;
}

const NO_NUMBERS: DetailNumbers = {
  leads: 0,
  booked: 0,
  households: 0,
  recurring: 0,
  reviewsAsked: 0,
  reviewsGiven: 0,
  ended: 0,
};

/**
 * The channel's own totals, added up from its roll-up rather than asked for a second time. The
 * roll-up groups the same households the channel-level report counted, so the sum of the details
 * IS the channel, and the panel can divide the channel's cost by it without a ninth statement.
 */
export function sumDetails(rows: readonly DetailNumbers[]): DetailNumbers {
  return rows.reduce(
    (total, row) => ({
      leads: total.leads + row.leads,
      booked: total.booked + row.booked,
      households: total.households + row.households,
      recurring: total.recurring + row.recurring,
      reviewsAsked: total.reviewsAsked + row.reviewsAsked,
      reviewsGiven: total.reviewsGiven + row.reviewsGiven,
      ended: total.ended + row.ended,
    }),
    NO_NUMBERS,
  );
}

/* ------------------------------------------------------------------ *
 * What the table has room for.
 * ------------------------------------------------------------------ */

/**
 * Whether all eleven columns fit, which is a question about the room the TABLE has and not about
 * the room the window has. A wide screen with a 400px panel open beside the table is narrower
 * where it matters than the same screen with nothing open: at the lg breakpoint exactly, the rail
 * and the page padding leave 728px, the panel and its gap take 420 of them, and eleven columns
 * divide 308px into shares of 18px to 40px. The table is fixed, so those words did not shrink,
 * they overflowed into the column beside them. The panel stands beside the table only from xl
 * since ops #56, and the table now scrolls in its own frame below the width its columns need, but
 * five readable columns are still a better answer than eleven to scroll through.
 *
 * So opening a channel takes the table back to the five that fit, and the channel that was opened
 * shows all ten of its numbers in the panel that caused it. Nothing is hidden by this; it moves.
 *
 * ONLY WHERE THE PANEL IS BESIDE THE TABLE, from xl (ops #56). Below xl the panel stands under
 * the table, which keeps the page's width, so a channel opened there leaves all eleven columns
 * where they were: the screen passes whether a channel is open AND the view is wide enough for
 * the panel to stand beside, never only the first.
 */
export function everyColumnFits(wider: boolean, panelIsBeside: boolean): boolean {
  return wider && !panelIsBeside;
}

/**
 * WHERE THE ROLL-UP FOR THE OPENED CHANNEL IS DRAWN, which is one place and never two. From md up
 * it is the split's panel, beside the table from xl and under it below xl (ops #56). Below md
 * there is no table and no panel beside anything: the
 * split lays the panel out AFTER the whole list, the list is never shorter than the ten channels
 * the plan funds, and each card is a heading and five rows of a two column list, so the answer to
 * a tap would land the better part of three screens below the finger that asked for it with
 * nothing scrolling to it. So on a phone it is drawn under the card that was pressed.
 *
 * Both shapes of the list are rendered at every width and hidden by a media query, so these two
 * are what keeps a desk from fetching the channel twice: once for the panel it can see and once
 * for a card it cannot. `rollUpBesideTheTable` and `rollUpUnderTheCard` are never both true.
 */
export function rollUpBesideTheTable(wide: boolean, channelIsOpen: boolean): boolean {
  return wide && channelIsOpen;
}

export function rollUpUnderTheCard(wide: boolean, thisRowIsOpen: boolean): boolean {
  return !wide && thisRowIsOpen;
}

/* ------------------------------------------------------------------ *
 * The order.
 * ------------------------------------------------------------------ */

export type SortKey =
  'channel' | 'leads' | 'booked' | 'households' | 'recurring' | 'cost' | 'ended';

export interface Ordering {
  readonly key: SortKey;
  readonly direction: 'ascending' | 'descending';
}

/**
 * What the screen opens on, and what the endpoint already sorted by: the channels that produced
 * households first. A source that has produced nothing is worth reading last, whatever it cost.
 */
export const FIRST_ORDER: Ordering = { key: 'households', direction: 'descending' };

/** Only the columns this sorts by. The rest of a row is read once the row is found. */
export interface ChannelNumbers {
  readonly channel: Channel;
  readonly leads: number;
  readonly booked: number;
  readonly households: number;
  readonly recurring: number;
  readonly costCents: number;
  readonly ended: number;
}

function compare(key: SortKey, a: ChannelNumbers, b: ChannelNumbers): number {
  if (key === 'channel') return channelWords(a.channel).localeCompare(channelWords(b.channel));
  if (key === 'cost') return a.costCents - b.costCents;
  return a[key] - b[key];
}

/**
 * Stable: rows the chosen column cannot separate keep the order the server gave them, which is
 * households then leads then the word itself. Sorting by a column of zeros therefore leaves the
 * list exactly as it was rather than shuffling it into an order nobody asked for.
 */
export function sortChannelRows<Row extends ChannelNumbers>(
  rows: readonly Row[],
  by: Ordering,
): readonly Row[] {
  const flip = by.direction === 'descending' ? -1 : 1;
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const step = compare(by.key, a.row, b.row) * flip;
      return step === 0 ? a.index - b.index : step;
    })
    .map((entry) => entry.row);
}

/* ------------------------------------------------------------------ *
 * Report B: the rules with a threshold, in words.
 * ------------------------------------------------------------------ */

/** What a cell says where a rule can no longer fire, so there is nothing left to count down. */
export const NOTHING_LEFT = 'Nothing left';

/**
 * The two ways the rule that runs over every funded source reads once a source has reached it.
 * "Exceeds" is the word the plan uses, so a source sitting exactly ON twice its planning figure
 * has not met the rule and does not say it has.
 */
export const AT_TWICE = 'At twice the figure';
export const OVER_TWICE = 'Over twice the figure';

/** A count in the unit its own rule counts in: ten leads, two hundred dollars, two households. */
export function countWords(unit: KillRuleUnit, value: number): string {
  switch (unit) {
    case 'leads':
      return value === 1 ? '1 lead' : `${value} leads`;
    case 'households':
      return value === 1 ? '1 household' : `${value} households`;
    case 'cents':
      return money(value);
    case 'multiple':
      return value === 1 ? '1 time' : `${value} times`;
  }
}

/**
 * WHAT IS LEFT, and the three different things an empty countdown can mean.
 *
 * A number is a countdown in the rule's own unit. Null is a rule that can no longer fire at all,
 * which is a dash and never a zero, for the same reason an unrecorded cost is: "a booking ended
 * this" and "it fires today" are opposite facts and a 0 in that cell would read as the second.
 *
 * And zero itself is read differently on the one rule that is a decision rather than a kill. Five
 * of the six count down to something being switched off; the sixth counts down to twice a
 * planning figure, where reaching it exactly is not yet exceeding it.
 */
export function remainingWords(
  rule: KillRuleId,
  unit: KillRuleUnit,
  remaining: number | null,
): string {
  if (remaining === null) return NOTHING_TO_SAY;
  if (rule === 'twice_the_planning_figure') {
    if (remaining < 0) return OVER_TWICE;
    if (remaining === 0) return AT_TWICE;
    return countWords(unit, remaining);
  }
  return remaining > 0 ? countWords(unit, remaining) : NOTHING_LEFT;
}

/**
 * Why a rule has no countdown, said in the words of that rule rather than left as a bare dash.
 *
 * The response carries no reason, and it does not have to: each rule has exactly one way of
 * reaching null, written into `api/src/reports/kill-rules.ts` beside the counter that causes it.
 * A booking stops both platform rules and the Facebook flight, a lead stops search ads and ChatGPT
 * ads, and the rule over every funded source has nothing to divide by until a household has
 * recurred.
 */
export function whyNothingIsLeft(rule: KillRuleId): string | null {
  switch (rule) {
    case 'local_services_leads':
    case 'thumbtack_leads':
    case 'facebook_ads_spend':
      return 'A booking ended this rule.';
    case 'search_ads_spend':
    case 'chatgpt_ads_spend':
      return 'A lead ended this rule.';
    case 'twice_the_planning_figure':
      return 'No recurring household on this source yet, so there is nothing to divide by.';
    case 'letter_december':
      // A dated decision counts households towards two and stops at zero. It never reaches null.
      return null;
  }
}

/** The day a rule would fire. Today where it already has, and a dash where nothing can say one. */
export function firesOnWords(firesOn: string | null, asOf: string): string {
  if (firesOn === null) return NOTHING_TO_SAY;
  if (firesOn === asOf) return 'Today';
  return formatDay(firesOn) ?? NOTHING_TO_SAY;
}

/**
 * The line under that day, which is the difference between a date and a forecast. A projection is
 * always in the future, since it is counted forward from today, so these two can never both be
 * true and the order they are tested in decides nothing.
 */
export function firesOnNote(
  firesOn: string | null,
  asOf: string,
  projected: boolean,
): string | null {
  if (firesOn === null) return null;
  if (projected) return 'Projected from the rate so far';
  return firesOn < asOf ? 'That day has passed' : null;
}

/**
 * THE ONE EXPRESSION ON THE SOURCES SCREEN THAT PAINTS ANYTHING CORAL.
 *
 * Coral means a gap somebody has to close, and an ordinary report number never is one: a channel
 * that produced nothing may be a channel to stop paying for, and that is a decision an owner makes
 * with the plan open rather than an alarm an interface raises. A rule near its threshold IS a gap,
 * because something has to be decided before it passes, so the rules section may use the colour
 * and the channel table below it may not.
 *
 * The decision itself is the server's and is not repeated here: two leads, twenty dollars and
 * seven days live in one file, `api/src/reports/kill-rules.ts`, beside the counters they judge.
 * This reads the answer. Nothing on this screen compares a number to a threshold of its own.
 */
export function ruleIsAGap(rule: { readonly withinReach: boolean }): boolean {
  return rule.withinReach;
}

/** One counter under its own name, so a screen prints them without knowing what any rule counts. */
export interface CounterLine {
  readonly label: string;
  readonly value: string;
}

/** The counters as they arrive: every rule names its own, and a null one is a real answer. */
export type RuleCounters = Readonly<Record<string, number | null>>;

/**
 * The counters a rule turns on, in reading order, each in the unit it is counted in.
 *
 * A counter that is null stays a dash. Cost per recurring household with no recurring household
 * is the case that matters: it is not $0.00, it is a division nobody can do, and $0.00 there is
 * the cheapest channel this business has ever had.
 *
 * AND THE MONTH'S SPEND ON THE TWICE-THE-FIGURE ROWS OBEYS THE COST COLUMN'S OWN RULE, which is
 * that a dash is not a zero. Those rows are drawn for every funded source whether anything was
 * typed against it or not, so an empty expenses table gave eight of them "Spent this month
 * $0.00", directly above a channel table whose cost column said "-" for the same eight channels:
 * one screen stating one fact two ways, and the confident half of it the false one. `amountCents`
 * is constrained positive on the server, so nothing but an unrecorded month can read as nought.
 *
 * The two money rules keep their $0.00, because their arithmetic genuinely counts money already
 * out: "$200 spent with zero leads" is a rule whose spend so far is nothing until a dollar goes.
 */
export function counterLines(rule: KillRuleId, counters: RuleCounters): readonly CounterLine[] {
  const count = (key: string): number => {
    const value = counters[key];
    return typeof value === 'number' ? value : 0;
  };
  const maybe = (key: string): number | null => {
    const value = counters[key];
    return typeof value === 'number' ? value : null;
  };

  switch (rule) {
    case 'local_services_leads':
    case 'thumbtack_leads':
      return [
        { label: 'Leads', value: String(count('leads')) },
        { label: 'Booked', value: String(count('bookings')) },
      ];
    case 'search_ads_spend':
    case 'chatgpt_ads_spend':
      return [
        { label: 'Spent', value: money(count('spendCents')) },
        { label: 'Leads', value: String(count('leads')) },
      ];
    case 'facebook_ads_spend':
      return [
        { label: 'Spent', value: money(count('spendCents')) },
        { label: 'Booked', value: String(count('bookings')) },
      ];
    case 'letter_december':
      return [
        { label: 'Households', value: String(count('households')) },
        { label: 'What rides on it', value: money(count('decisionCents')) },
      ];
    case 'twice_the_planning_figure': {
      const each = maybe('costPerRecurringCents');
      const spent = count('costCents');
      const planned = planningRangeWords({
        lowCents: maybe('planningLowCents'),
        highCents: maybe('planningHighCents'),
      });
      return [
        { label: 'Spent this month', value: spent > 0 ? money(spent) : NOTHING_TO_SAY },
        { label: 'Recurring', value: String(count('recurring')) },
        { label: 'Per recurring', value: each === null ? NOTHING_TO_SAY : money(each) },
        { label: 'Planned', value: planned ?? NOTHING_TO_SAY },
      ];
    }
  }
}

/**
 * The channel a rule belongs to, in the one spelling the rest of this product uses. Report B sends
 * the channel as a plain string rather than as the union, so a word this interface has never heard
 * of is printed as it arrived: a rule drawn against `undefined` is worse than one drawn against a
 * word somebody can go and look up.
 */
export function ruleChannelWords(channel: string): string {
  const known = CHANNELS.find((word) => word === channel);
  return known === undefined ? channel : CHANNEL_LABELS[known];
}

/** The four fields a note is grouped and labelled by. */
export interface RuleNoteRow {
  readonly rule: KillRuleId;
  readonly channel: string;
  readonly ruleText: string;
  readonly note: string | null;
}

export interface RuleNote {
  readonly key: string;
  readonly label: string;
  readonly note: string;
}

/**
 * THE NOTES, SAID ONCE EACH AND UNDER THE TABLE RATHER THAN INSIDE IT.
 *
 * Three of the six rules carry a paragraph in ops's own words: what Thumbtack's real measure is
 * and why this system cannot count it, that the search-ads rule has two ways to fire, and that
 * cost per recurring household is judged on a month rather than counted down to. Each is several
 * lines of prose, and a column holding several lines of prose is a table nobody scans.
 *
 * The last of those three rides on EVERY funded source, which is one row per channel and up to
 * eight copies of one paragraph. So notes are grouped by the words themselves: a note that appears
 * once is labelled with the channel it belongs to, and a note that appears on several rows is
 * labelled with the rule, because a paragraph shared by eight channels is about the rule and not
 * about any one of them.
 */
export function ruleNotes(rules: readonly RuleNoteRow[]): readonly RuleNote[] {
  const seen = new Map<string, { row: RuleNoteRow; count: number }>();
  for (const row of rules) {
    if (row.note === null || row.note.trim() === '') continue;
    const found = seen.get(row.note);
    if (found) found.count += 1;
    else seen.set(row.note, { row, count: 1 });
  }
  return [...seen.entries()].map(([note, { row, count }]) => ({
    key: `${row.rule}-${row.channel}`,
    label: count === 1 ? ruleChannelWords(row.channel) : row.ruleText,
    note,
  }));
}

/* ------------------------------------------------------------------ *
 * The one line a rule near firing gets on the Today screen.
 * ------------------------------------------------------------------ */

/**
 * WHAT THE TODAY PANEL'S NUMBER IS COUNTED IN, and why the answer is not in the response.
 *
 * The cheap endpoint answers four fields per rule and the unit is not one of them, deliberately:
 * three statements is what lets that panel sit beside the four the screen already fetches on a
 * desk. So the SPELLING of the number lives here, keyed by the channel whose counted rule it is,
 * and it is a spelling and never a threshold. Nothing in this file knows what any rule counts to,
 * and a channel with no entry prints the bare number the server sent rather than a wrong unit.
 */
const COUNTED_UNITS: Readonly<Record<string, KillRuleUnit>> = Object.freeze({
  local_services: 'leads',
  thumbtack: 'leads',
  search_ads: 'cents',
  facebook_ads: 'cents',
  chatgpt_ads: 'cents',
  letter: 'households',
});

/** The four fields the cheap endpoint answers with, of which this reads three. */
export interface WithinReachNumbers {
  readonly channel: string;
  readonly remaining: number | null;
  readonly firesOn: string | null;
}

/**
 * The mark beside a rule on Today: what is left, the day it would fire, or BOTH, because the panel
 * cannot know which of the two put the row in front of it.
 *
 * A countdown alone was wrong, and quietly. `api/src/reports/kill-rules.ts` marks the search-ads
 * rule within reach when EITHER the money is close OR the thirty-day date is, and the December
 * letter on its date alone. Twenty dollars into a two hundred dollar flight, three days from the
 * thirtieth day, a mark reading "$180.00" sits under a heading saying a rule is close to firing
 * and looks nowhere near it: the one thing making the row true is the half that was dropped. The
 * Sources row carries both. So does this one now.
 *
 * The countdown still comes first, because it is the thing running out and the Facebook flight has
 * no day at all: its rule names money and nothing else. The day is relative rather than a date,
 * since this is the screen about today, and it says so in its own words when it has already gone.
 *
 * NO THRESHOLD IS REPEATED HERE. Which half is near is the server's judgement and it is not
 * recomputed to decide what to print; both halves are printed and the reader decides. And the four
 * fields carry no `projected`, so the day cannot be marked as the forecast three of the five
 * counted rules make of it, which is the second reason the countdown leads and is never dropped
 * for it. The Sources screen carries the same two numbers with the projection said out loud.
 */
export function withinReachWords(row: WithinReachNumbers, today: string): string {
  const unit = COUNTED_UNITS[row.channel];
  const countdown =
    row.remaining !== null && row.remaining > 0
      ? unit === undefined
        ? String(row.remaining)
        : countWords(unit, row.remaining)
      : null;
  const days = row.firesOn === null ? null : daysBetween(today, row.firesOn);
  if (days === null) return countdown ?? NOTHING_LEFT;
  if (days < 0) return countdown === null ? 'Passed' : `${countdown}, and that day has passed`;
  if (days === 0) return countdown === null ? 'Today' : `${countdown}, or today`;
  const ahead = days === 1 ? 'in 1 day' : `in ${days} days`;
  return countdown === null ? ahead : `${countdown}, or ${ahead}`;
}

/**
 * WHETHER THE PANEL IS DRAWN AT ALL, which is three answers and not two.
 *
 * It is not a notification and it is not a permanent fixture: on almost every morning of this
 * business no rule is near firing, and a panel saying so is a panel somebody stops reading. So
 * nothing is drawn while the answer is still coming and nothing is drawn when the answer is none.
 *
 * A refusal is the third answer and is drawn, because "no rule is close" and "this did not load"
 * are different facts and silence would say the first while meaning the second.
 */
export type PanelDraw = 'nothing' | 'rules' | 'refusal';

export function closeToFiringPanel(loaded: boolean, count: number, failed: boolean): PanelDraw {
  if (failed) return 'refusal';
  return loaded && count > 0 ? 'rules' : 'nothing';
}

/* ------------------------------------------------------------------ *
 * What a spend row does to a channel's cost, said where it is typed.
 * ------------------------------------------------------------------ */

/**
 * Whether report A will count this row against the channel typed on it.
 *
 * Statement 5 filters on category `marketing`, and the category is the filter rather than
 * decoration: `phone` is BOTH an expense category and a channel, so a phone bill typed with both
 * would land in cost per household for every call the business ever took. The consequence is that
 * a letter batch filed under anything but marketing counts towards nothing at all, which is worth
 * saying under the box rather than leaving somebody to find in a report that reads a dash.
 */
export function countsAsChannelCost(category: ExpenseCategory, channel: Channel | null): boolean {
  return channel !== null && category === 'marketing';
}

/** That consequence in a sentence, or nothing where the row is unremarkable. */
export function channelCostNote(category: ExpenseCategory, channel: Channel | null): string | null {
  if (channel === null || countsAsChannelCost(category, channel)) return null;
  return 'This names a channel but is not marketing, so no channel counts it as cost.';
}

/**
 * A DAY THAT HAS NOT HAPPENED IS REFUSED WHERE IT IS TYPED, because nothing downstream would ever
 * show it again.
 *
 * Every window this product reports over ends at the server's own day: all four presets above end
 * at `today` or earlier, report A closes on the day it is asked about, and report B counts spend
 * up to `asOf` and no further. A row dated next month is therefore recorded, audited, and then
 * invisible in every period a person can press, and the empty list under it reads "Nothing typed
 * in for these days ... Add one above", which is an invitation to type the same money in twice.
 *
 * The screen refuses it in a sentence naming the box, and the date box carries the same day as its
 * maximum so the calendar will not offer one. This is the one refusal on that form the server does
 * not make as well: a day is a day to `expenses`, and what is wrong with this one is not its shape
 * but that no report would count it.
 */
export const SPENT_IN_THE_FUTURE =
  'The day the money was spent has not come yet. Every period this screen offers ends today, so that row would be recorded and then shown nowhere.';

export const PAID_IN_THE_FUTURE =
  'The day it was paid has not come yet. Leave it empty until the money has actually gone.';

/** The sentence for whichever of the two days is wrong, or nothing where both are behind us. */
export function spendDayProblem(incurredOn: string, paidOn: string, today: string): string | null {
  if (isDay(incurredOn) && isDay(today) && incurredOn > today) return SPENT_IN_THE_FUTURE;
  if (paidOn !== '' && isDay(paidOn) && isDay(today) && paidOn > today) return PAID_IN_THE_FUTURE;
  return null;
}
